// 🌿 Herbicide Products Database for Timing Calculator
// Comprehensive product information for weed control recommendations

import { amazonUrl } from '@/lib/affiliate'

export interface HerbicideProduct {
  name: string
  activeIngredients: string[]
  type: 'pre-emergent' | 'post-emergent' | 'selective' | 'non-selective'
  targetWeeds: string[]
  safeGrassTypes: string[]
  applicationRate: string // "per 1000 sq ft"
  timing: string
  notes: string[]
  costEstimate: {
    per1000SqFt: number // USD
    productSize: string
    typicalCost: number // USD for full product
  }
  safetyRating: 'low' | 'moderate' | 'high'
  effectiveness: 'excellent' | 'good' | 'fair'
  affiliateUrl?: string
}

export const HERBICIDE_PRODUCTS: Record<string, HerbicideProduct> = {
  'Prodiamine 0-0-7': {
    name: 'Prodiamine 65 WDG (Generic Barricade)',
    activeIngredients: ['Prodiamine'],
    type: 'pre-emergent',
    targetWeeds: ['Crabgrass', 'Foxtail (Setaria)', 'Goosegrass', 'Annual Bluegrass (Poa annua)', 'Spotted Spurge'],
    safeGrassTypes: ['Kentucky Bluegrass', 'Tall Fescue', 'Fine Fescue', 'Perennial Ryegrass', 'Bermudagrass', 'Zoysiagrass', 'St. Augustinegrass', 'Centipede', 'Bahiagrass', 'Kikuyugrass', 'Mixed Cool-Season Blend'],
    applicationRate: '0.185-0.83 oz per 1000 sq ft (65 WDG concentrate)',
    timing: 'Late winter to early spring, before soil reaches 55°F',
    affiliateUrl: amazonUrl('B015BSNZP2'),
    notes: [
      'Long-lasting residual control (4-6 months)',
      'Safe for established lawns',
      'Apply before weed seeds germinate',
      'Water in thoroughly after application',
      'Kikuyugrass: labeled tolerant, but confirm on your specific product label'
    ],
    costEstimate: {
      per1000SqFt: 5.00,
      productSize: '50 lb bag',
      typicalCost: 85
    },
    safetyRating: 'moderate',
    effectiveness: 'excellent'
  },

  'Dimension 2EW': {
    name: 'Dimension 2EW',
    affiliateUrl: amazonUrl('B0051GXXPW'),
    activeIngredients: ['Dithiopyr'],
    type: 'pre-emergent',
    targetWeeds: ['Crabgrass', 'Foxtail (Setaria)', 'Goosegrass', 'Annual Bluegrass (Poa annua)', 'Spotted Spurge'],
    safeGrassTypes: ['Kentucky Bluegrass', 'Tall Fescue', 'Fine Fescue', 'Perennial Ryegrass', 'Bermudagrass', 'Zoysiagrass', 'St. Augustinegrass', 'Centipede', 'Bahiagrass', 'Kikuyugrass', 'Mixed Cool-Season Blend'],
    applicationRate: '2-3 fl oz per 1000 sq ft',
    timing: 'Late winter to early spring, before soil reaches 55°F',
    notes: [
      'Excellent pre-emergent control',
      'Some post-emergent activity on young weeds',
      'Safe for most cool-season grasses',
      'Apply before soil reaches 55°F',
      'Kikuyugrass: labeled tolerant, but confirm on your specific product label'
    ],
    costEstimate: {
      per1000SqFt: 8.00,
      productSize: '1 gallon',
      typicalCost: 120
    },
    safetyRating: 'moderate',
    effectiveness: 'excellent'
  },

  'Barricade 4FL': {
    name: 'Barricade 4FL',
    affiliateUrl: amazonUrl('B00OXD2CKG'),
    activeIngredients: ['Prodiamine'],
    type: 'pre-emergent',
    targetWeeds: ['Crabgrass', 'Foxtail (Setaria)', 'Goosegrass', 'Broadleaf Plantain', 'Annual Bluegrass (Poa annua)', 'Spotted Spurge'],
    safeGrassTypes: ['Kentucky Bluegrass', 'Tall Fescue', 'Fine Fescue', 'Perennial Ryegrass', 'Bermudagrass', 'Zoysiagrass', 'St. Augustinegrass', 'Centipede', 'Bahiagrass', 'Kikuyugrass', 'Mixed Cool-Season Blend'],
    applicationRate: '1.5-2 fl oz per 1000 sq ft',
    timing: 'Late winter to early spring',
    notes: [
      'Liquid formulation for easy application',
      'Long-lasting residual control',
      'Professional-grade product',
      'Apply before soil reaches 55°F',
      'Kikuyugrass: labeled tolerant, but confirm on your specific product label'
    ],
    costEstimate: {
      per1000SqFt: 6.50,
      productSize: '2.5 gallon',
      typicalCost: 180
    },
    safetyRating: 'moderate',
    effectiveness: 'excellent'
  },

  '2,4-D Amine': {
    name: 'Hi-Yield 2,4-D Selective Weed Killer',
    affiliateUrl: amazonUrl('B0058V1IV6'),
    activeIngredients: ['2,4-D'],
    type: 'post-emergent',
    targetWeeds: ['Dandelions', 'White Clover', 'Broadleaf Plantain', 'Henbit', 'Chickweed (Common)', 'Spotted Spurge'],
    safeGrassTypes: ['Kentucky Bluegrass', 'Tall Fescue', 'Fine Fescue', 'Perennial Ryegrass', 'Bermudagrass', 'Zoysiagrass', 'St. Augustinegrass', 'Centipede', 'Annual Ryegrass', 'Mixed Cool-Season Blend'],
    applicationRate: '2-4 fl oz per 1000 sq ft',
    timing: 'Spring or fall when weeds are actively growing',
    notes: [
      'Selective herbicide for broadleaf weeds',
      'Safe for most established grass types including warm-season',
      'Apply when weeds are actively growing (65-85°F)',
      'Use as spot treatment on warm-season grasses to minimize stress',
      'Avoid application during hot weather or spring green-up'
    ],
    costEstimate: {
      per1000SqFt: 3.00,
      productSize: '1 gallon',
      typicalCost: 45
    },
    safetyRating: 'moderate',
    effectiveness: 'good'
  },

  'Trimec Classic': {
    name: 'Trimec Classic',
    affiliateUrl: amazonUrl('B015BSWOV8'),
    activeIngredients: ['2,4-D', 'MCPP', 'Dicamba'],
    type: 'post-emergent',
    targetWeeds: ['Dandelions', 'White Clover', 'Broadleaf Plantain', 'Henbit', 'Chickweed (Common)', 'Spotted Spurge', 'Wild Violet'],
    safeGrassTypes: ['Kentucky Bluegrass', 'Tall Fescue', 'Fine Fescue', 'Perennial Ryegrass', 'Bermudagrass', 'Annual Ryegrass', 'Mixed Cool-Season Blend'],
    applicationRate: '2-4 fl oz per 1000 sq ft',
    timing: 'Spring or fall when weeds are actively growing',
    notes: [
      'Three-way herbicide combination',
      'Effective on many broadleaf weeds',
      'Apply when weeds are actively growing',
      'Avoid application during hot, dry weather'
    ],
    costEstimate: {
      per1000SqFt: 4.00,
      productSize: '1 gallon',
      typicalCost: 65
    },
    safetyRating: 'moderate',
    effectiveness: 'excellent'
  },

  'Tenacity': {
    name: 'Torocity Mesotrione (Generic Tenacity)',
    affiliateUrl: amazonUrl('B09SBR4BBR'),
    activeIngredients: ['Mesotrione'],
    type: 'post-emergent',
    targetWeeds: ['Crabgrass', 'Foxtail (Setaria)', 'Goosegrass', 'White Clover'],
    safeGrassTypes: ['Kentucky Bluegrass', 'Tall Fescue', 'Fine Fescue', 'Perennial Ryegrass', 'Centipede', 'Mixed Cool-Season Blend'],
    applicationRate: '0.5-1 fl oz per 1000 sq ft',
    timing: 'Spring or summer when weeds are young',
    notes: [
      'Selective herbicide for grassy and broadleaf weeds',
      'Causes temporary whitening of grass (recovers in 7-14 days)',
      'NOT safe for Bermudagrass or Bahiagrass',
      'Use with caution on Zoysiagrass (injury may occur)',
      'Apply when weeds are young and actively growing'
    ],
    costEstimate: {
      per1000SqFt: 12.00,
      productSize: '8 fl oz',
      typicalCost: 85
    },
    safetyRating: 'moderate',
    effectiveness: 'excellent'
  },

  'Roundup Ready-To-Use': {
    name: 'Roundup Ready-To-Use',
    // Affiliate link removed 2026-07-11: the amzn.to shortlink resolved to
    // 'Roundup for Lawns' (a SELECTIVE northern-lawn product), which
    // contradicts this entry's non-selective glyphosate semantics. Unlinked
    // beats mislinked; relink only to a PAAPI-verified glyphosate RTU.
    activeIngredients: ['Glyphosate'],
    type: 'non-selective',
    targetWeeds: ['All weeds'],
    safeGrassTypes: ['None - kills all vegetation'],
    applicationRate: 'Spray to wet foliage',
    timing: 'When weeds are actively growing',
    notes: [
      'Non-selective herbicide - kills all vegetation',
      'Use only for spot treatment or renovation',
      'Apply only to weeds, avoid grass',
      'Allow 7-10 days before reseeding'
    ],
    costEstimate: {
      per1000SqFt: 15.00,
      productSize: '1 gallon',
      typicalCost: 25
    },
    safetyRating: 'low',
    effectiveness: 'excellent'
  },

  'Quinclorac 75DF': {
    name: 'Quinclorac 75DF',
    affiliateUrl: amazonUrl('B006LAVM3W'),
    activeIngredients: ['Quinclorac'],
    type: 'post-emergent',
    targetWeeds: ['Crabgrass', 'Foxtail (Setaria)', 'Goosegrass'],
    safeGrassTypes: ['Kentucky Bluegrass', 'Tall Fescue', 'Fine Fescue', 'Perennial Ryegrass', 'Bermudagrass', 'Zoysiagrass', 'Buffalograss', 'Mixed Cool-Season Blend'],
    applicationRate: '0.5-1 oz per 1000 sq ft',
    timing: 'Summer when weeds are actively growing',
    notes: [
      'Selective herbicide for grassy weeds',
      'Effective on mature crabgrass',
      'Safe for Bermuda and Zoysia lawns',
      'NOT safe for St. Augustinegrass, Centipede, or Bahiagrass',
      'Apply when weeds are actively growing, below 90°F'
    ],
    costEstimate: {
      per1000SqFt: 6.00,
      productSize: '1 lb',
      typicalCost: 75
    },
    safetyRating: 'moderate',
    effectiveness: 'good'
  },

  'SpeedZone': {
    name: 'SpeedZone Broadleaf Herbicide',
    affiliateUrl: amazonUrl('B001PCRKDC'),
    activeIngredients: ['2,4-D', 'MCPP', 'Dicamba', 'Carfentrazone'],
    type: 'post-emergent',
    targetWeeds: ['Dandelions', 'White Clover', 'Broadleaf Plantain', 'Henbit', 'Chickweed (Common)', 'Spotted Spurge', 'Wild Violet'],
    safeGrassTypes: ['Kentucky Bluegrass', 'Tall Fescue', 'Fine Fescue', 'Perennial Ryegrass', 'Annual Ryegrass', 'Mixed Cool-Season Blend'],
    applicationRate: '2-4 fl oz per 1000 sq ft',
    timing: 'Spring or fall when weeds are actively growing',
    notes: [
      'Four-way herbicide combination',
      'Fast-acting with visible results',
      'Apply when weeds are actively growing',
      'Avoid application during hot weather'
    ],
    costEstimate: {
      per1000SqFt: 5.50,
      productSize: '1 gallon',
      typicalCost: 75
    },
    safetyRating: 'moderate',
    effectiveness: 'excellent'
  },

  'SedgeHammer': {
    name: 'SedgeHammer',
    affiliateUrl: amazonUrl('B007PHNUOE'),
    activeIngredients: ['Halosulfuron-methyl'],
    type: 'post-emergent',
    targetWeeds: ['Nutsedge', 'Kyllinga', 'Broadleaf Plantain'],
    safeGrassTypes: ['Kentucky Bluegrass', 'Tall Fescue', 'Fine Fescue', 'Perennial Ryegrass', 'Bermudagrass', 'Zoysiagrass', 'St. Augustinegrass', 'Centipede', 'Bahiagrass', 'Kikuyugrass', 'Mixed Cool-Season Blend'],
    applicationRate: '0.5-1 tsp per 1000 sq ft',
    timing: 'Summer when sedges are actively growing',
    notes: [
      'Specialized herbicide for sedges and nutsedge',
      'Safe for both cool-season and warm-season grasses',
      'Apply when sedges are actively growing',
      'Use with surfactant for best results',
      'May require multiple applications 6-8 weeks apart'
    ],
    costEstimate: {
      per1000SqFt: 10.00,
      productSize: '1.3 oz',
      typicalCost: 55
    },
    safetyRating: 'moderate',
    effectiveness: 'excellent'
  },

  'Triclopyr 4EC': {
    name: 'Triclopyr 4EC (Turflon Ester)',
    affiliateUrl: amazonUrl('B009ATET6I'),
    activeIngredients: ['Triclopyr'],
    type: 'post-emergent',
    targetWeeds: ['Wild Violet', 'Creeping Charlie (Ground Ivy)', 'White Clover', 'Broadleaf Plantain'],
    safeGrassTypes: ['Kentucky Bluegrass', 'Tall Fescue', 'Fine Fescue', 'Perennial Ryegrass', 'Mixed Cool-Season Blend'],
    applicationRate: '0.5-1 fl oz per 1000 sq ft',
    timing: 'Fall (October-November) for best results on difficult weeds',
    notes: [
      'Best option for wild violet and creeping charlie',
      'Triclopyr penetrates waxy leaves better than 2,4-D',
      'Fall application is significantly more effective than spring',
      'Do NOT use on bermudagrass, zoysiagrass, or other warm-season grasses'
    ],
    costEstimate: {
      per1000SqFt: 4.50,
      productSize: '1 quart',
      typicalCost: 45
    },
    safetyRating: 'moderate',
    effectiveness: 'excellent'
  }
}

// Helper functions for product selection
export function getProductsByType(type: 'pre-emergent' | 'post-emergent' | 'selective' | 'non-selective'): HerbicideProduct[] {
  return Object.values(HERBICIDE_PRODUCTS).filter(product => product.type === type)
}

export function getProductsForWeeds(targetWeeds: string[]): HerbicideProduct[] {
  return Object.values(HERBICIDE_PRODUCTS).filter(product => 
    targetWeeds.some(weed => product.targetWeeds.includes(weed))
  )
}

export function getProductsForGrassType(grassType: string): HerbicideProduct[] {
  return Object.values(HERBICIDE_PRODUCTS).filter(product => 
    product.safeGrassTypes.includes(grassType)
  )
}

export function getProductsBySafetyRating(rating: 'low' | 'moderate' | 'high'): HerbicideProduct[] {
  return Object.values(HERBICIDE_PRODUCTS).filter(product => product.safetyRating === rating)
}

export function getProductsByEffectiveness(rating: 'excellent' | 'good' | 'fair'): HerbicideProduct[] {
  return Object.values(HERBICIDE_PRODUCTS).filter(product => product.effectiveness === rating)
}
